<template>
  <v-container fluid class="pa-0 fill-height">
    <v-app-bar app color="primary" dark flat>
      <v-toolbar-title class="font-weight-bold"
        >{{ user.name }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <!-- <v-btn color="secondary" rounded small>
        <v-icon left>mdi-pencil</v-icon>Edit
      </v-btn> -->
    </v-app-bar>

    <v-row justify="center" class="py-5">
      <v-col cols="12" class="text-center">
        <v-chip
          small
          color="accent lighten-5"
          text-color="primary"
          class="mt-1 font-weight-medium"
        >
          Registered User
        </v-chip>
      </v-col>
    </v-row>

    <!-- 🧭 Options -->
    <v-card flat class="mx-4 mb-6 option-card">
      <v-list two-line>
        <v-list-item @click="openMatchmaking">
          <v-list-item-avatar color="primary" class="white--text">
            <v-icon dark>mdi-heart-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium text--primary">
              Matchmaking Preferences
            </v-list-item-title>
            <v-list-item-subtitle>
              Update your search preferences
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
          </v-list-item-icon>
        </v-list-item>

        <v-divider inset></v-divider>

        <v-list-item @click="openAccountSettings">
          <v-list-item-avatar color="primary" class="white--text">
            <v-icon dark>mdi-cog-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium text--primary">
              Account Settings
            </v-list-item-title>
            <v-list-item-subtitle>
              Manage your email and password
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
          </v-list-item-icon>
        </v-list-item>

        <v-divider inset></v-divider>

        <v-list-item @click="openFeedback">
          <v-list-item-avatar color="primary" class="white--text">
            <v-icon dark>mdi-message-text-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium text--primary">
              Feedback
            </v-list-item-title>
            <v-list-item-subtitle>
              Share your experience or report an issue
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon color="grey darken-1">mdi-chevron-right</v-icon>
          </v-list-item-icon>
        </v-list-item>
      </v-list>
    </v-card>

    <v-card flat class="mx-4 mb-10 option-card pa-4">
      <v-list subheader>
        <div class="d-flex justify-space-between align-center">
          <v-subheader class="font-weight-bold text--primary mb-0">
            Your Profiles
          </v-subheader>

          <!-- ➕ Add New Profile -->
          <v-btn
            icon
            color="primary"
            @click="createProfile"
            class="mr-2"
            :disabled="profiles.length >= 5"
          >
            <v-icon>mdi-account-plus</v-icon>
          </v-btn>
        </div>

        <!-- ✅ Profiles Exist -->
        <template v-if="profiles.length">
          <v-card rounded="xl" outlined>
            <template v-for="(profile, index) in profiles">
              <v-list-item class="align-center" :key="profile.id || index">
                <!-- 👤 Profile Avatar -->
                <v-list-item-avatar color="secondary" class="white--text">
                  <v-img
                    v-if="profile.gender === 'Female'"
                    src="@/assets/avatars/wife.png"
                    contain
                  />
                  <v-img v-else src="@/assets/avatars/brother.png" contain />
                </v-list-item-avatar>

                <!-- 👤 Profile Info -->
                <v-list-item-content
                  @click="openProfile(profile)"
                  class="cursor-pointer"
                >
                  <v-list-item-title
                    class="font-weight-medium text--primary text-capitalize"
                  >
                    {{ profile.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="grey--text text--darken-1">
                    Created on {{ formatDate(profile.createdAt) }}
                    <span v-if="profile.createdAt" class="ml-1">
                      — {{ timeAgo(profile.createdAt) }}
                    </span>
                  </v-list-item-subtitle>

                  <!-- 🟢 Active / Paused chip stays the same -->
                  <v-chip
                    x-small
                    :color="
                      profile.isActive ? 'green lighten-5' : 'grey lighten-4'
                    "
                    :text-color="
                      profile.isActive ? 'green darken-2' : 'grey darken-2'
                    "
                    class="mt-1"
                  >
                    <v-icon left x-small>
                      {{
                        profile.isActive
                          ? "mdi-check-circle"
                          : "mdi-pause-circle"
                      }}
                    </v-icon>
                    {{ profile.isActive ? "Active" : "Paused" }}
                  </v-chip>

                  <!-- 🕒 Progress bar for pending approval -->
                  <div
                    v-if="profile.approvalStatus === 'pending'"
                    class="mt-2 pr-6"
                  >
                    <v-progress-linear
                      :value="getApprovalProgress(profile)"
                      height="6"
                      color="amber"
                      rounded
                    ></v-progress-linear>

                    <div class="text-caption grey--text mt-1">
                      {{ getApprovalCountdown(profile) }}
                    </div>
                  </div>
                </v-list-item-content>

                <!-- ⚙️ Menu for Actions -->
                <v-list-item-action>
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon color="grey darken-1">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>

                    <v-list dense>
                      <!-- ✏️ Edit -->
                      <v-list-item @click.stop="editProfile(profile)">
                        <v-list-item-title>
                          <v-icon left small color="primary">mdi-pencil</v-icon>
                          Edit Profile
                        </v-list-item-title>
                      </v-list-item>

                      <v-divider></v-divider>

                      <!-- ⏸ Pause / Activate -->
                      <v-list-item @click.stop="toggleProfile(profile)">
                        <v-list-item-title>
                          <v-icon left small>
                            {{
                              profile.isActive
                                ? "mdi-pause-circle"
                                : "mdi-play-circle"
                            }}
                          </v-icon>
                          {{
                            profile.isActive
                              ? "Pause Profile"
                              : "Activate Profile"
                          }}
                        </v-list-item-title>
                      </v-list-item>

                      <v-divider></v-divider>

                      <!-- 🗑 Delete -->
                      <v-list-item @click.stop="deleteProfile(profile)">
                        <v-list-item-title class="red--text">
                          <v-icon left small color="red darken-2"
                            >mdi-delete</v-icon
                          >
                          Delete Profile
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>

              <!-- 🔹 Divider between profiles -->
              <v-divider
                v-if="index < profiles.length - 1"
                inset
                class="ml-12 mr-4"
              ></v-divider>
            </template>
          </v-card>
        </template>

        <!-- 🚫 No Profiles Yet -->
        <template v-else>
          <div class="text-center pa-8">
            <v-icon size="48" color="grey lighten-1"
              >mdi-account-outline</v-icon
            >
            <div class="mt-3 grey--text text--darken-1">
              You haven’t created any profiles yet.
            </div>
            <v-btn
              color="primary"
              rounded
              large
              class="mt-4 px-6"
              @click="createProfile"
            >
              <v-icon left>mdi-account-plus</v-icon>
              Create Your First Profile
            </v-btn>
          </div>
        </template>
      </v-list>

      <!-- 🕌 Guardian Message -->
      <div class="text-caption text-center mt-4 grey--text text--darken-1">
        <v-icon small color="primary" class="mr-1"
          >mdi-information-outline</v-icon
        >
        This app is designed for <strong>Wali (guardians or parents)</strong>.
        You can manage up to <strong>5 profiles</strong> for your children or
        family members.<br />
        When messages or matches are received,
        <strong>you will be notified</strong>
        as their guardian.
      </div>
    </v-card>
  </v-container>
</template>

<script>
import userService from "@/modules/User/service"; // ✅ adjust path if needed
import { mapGetters } from "vuex/dist/vuex.common.js";
import profileService from "@/modules/Profile/service.js";

export default {
  data() {
    return {
      user: {
        name: "Muhammad Saqib",
        id: null, // will get from store or backend
      },
      profiles: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters("global", [
      "currentUser",
      "currentOrganization",
      "globalStatus",
      "loadingStatus",
      "loginDialog",
    ]),
  },
  async created() {
    this.user = this.currentUser;
    await this.loadProfiles();
  },

  methods: {
    getApprovalProgress(profile) {
      if (!profile.metadata || !profile.metadata.approvalDeadline) return 0;

      const created = new Date(profile.createdAt);
      const deadline = new Date(profile.metadata.approvalDeadline);
      const now = new Date();

      const total = deadline - created;
      const elapsed = Math.max(0, now - created);
      const progress = Math.min(100, (elapsed / total) * 100);

      return progress;
    },

    getApprovalCountdown(profile) {
      if (!profile.metadata || !profile.metadata.approvalDeadline) return "";

      const deadline = new Date(profile.metadata.approvalDeadline);
      const now = new Date();
      const diffMs = deadline - now;
      if (diffMs <= 0) return "Will activate shortly…";

      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0)
        return `Will be active in ${diffDays} day${diffDays > 1 ? "s" : ""}`;
      if (diffHours > 0)
        return `Will be active in ${diffHours} hour${diffHours > 1 ? "s" : ""}`;
      return `Will be active in ${diffMins} minute${diffMins > 1 ? "s" : ""}`;
    },
    editProfile(profile) {
      this.$router.push({
        name: "profile-edit",
        params: { profileId: profile.id },
      });
    },
    formatDate(date) {
      if (!date) return "—";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    timeAgo(date) {
      if (!date) return "";
      const now = new Date();
      const past = new Date(date);
      const diff = Math.floor((now - past) / (1000 * 60 * 60 * 24)); // days

      if (diff < 1) return "Today";
      if (diff === 1) return "1 day ago";
      if (diff < 30) return `${diff} days ago`;
      const months = Math.floor(diff / 30);
      if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
      const years = Math.floor(months / 12);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    },
    async loadProfiles() {
      try {
        this.loading = true;

        // ✅ If you have Vuex auth module or localStorage
        const currentUser = JSON.parse(localStorage.getItem("user"));
        if (currentUser) this.user = currentUser;

        // ✅ Fetch profiles for the user
        const result = await userService.getProfilesByUser(this.user.id);

        if (result && result.data) {
          this.profiles = result.data;
        } else {
          this.profiles = [];
        }
      } catch (err) {
        console.error("Error fetching profiles:", err);
        this.profiles = [];
      } finally {
        this.loading = false;
      }
    },
    async toggleProfile(profile) {
      const confirm = await this.$swal({
        title: profile.isActive
          ? "Pause this profile?"
          : "Reactivate this profile?",
        text: profile.isActive
          ? "Your profile will be hidden from others until you reactivate it."
          : "Your profile will be visible again.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, continue",
      });

      if (!confirm.isConfirmed) return;

      try {
        const result = await profileService.toggleActive(profile.id, {
          isActive: !profile.isActive,
        });
        profile.isActive = !profile.isActive;

        this.$swal({
          icon: "success",
          title: profile.isActive ? "Profile reactivated" : "Profile paused",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        this.$swal({
          icon: "error",
          title: "Unable to update profile status",
        });
      }
    },

    async deleteProfile(profile) {
      const confirm = await this.$swal({
        title: "Delete Profile?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) return;

      try {
        await profileService.delete(profile.id);
        this.profiles = this.profiles.filter((p) => p.id !== profile.id);

        this.$swal({
          icon: "success",
          title: "Profile deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        this.$swal({
          icon: "error",
          title: "Failed to delete profile",
        });
      }
    },
    // editProfile(id) {
    //   this.$router.push({
    //     name: "profile-edit",
    //     params: { profileId: this.profile.id },
    //   });
    // },
    openMatchmaking() {
      this.$router.push("/user/matchmaking-preferences");
    },
    openAccountSettings() {
  this.$swal({
    title: "Coming Soon!",
    text: "We’re working on the Account Settings feature. Thanks for your patience!",
    icon: "info",
    confirmButtonText: "Okay",
    confirmButtonColor: "#2A6465",
  });
},

    async openFeedback() {
      const { value: feedbackText } = await this.$swal.fire({
        title: "We’d love your feedback 💬",
        input: "textarea",
        inputPlaceholder: "Share your thoughts or suggestions...",
        inputAttributes: {
          "aria-label": "Type your feedback here",
          maxlength: 500,
        },
        showCancelButton: true,
        confirmButtonText: "Submit",
        confirmButtonColor: "#2A6465",
      });

      if (feedbackText) {
        try {
          await userService.submitFeedback({
            user: { id: this.currentUser.id },
            feedbackText,
          });

          this.$swal.fire({
            icon: "success",
            title: "Thank You!",
            text: "Your feedback has been submitted successfully.",
            confirmButtonColor: "#2A6465",
          });
        } catch (err) {
          console.error("Feedback error:", err);
          this.$swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong while sending feedback.",
            confirmButtonColor: "#E53935",
          });
        }
      }
    },
    createProfile() {
      this.$router.push("/profile/create");
    },
    openProfile(profile) {
      this.$router.push({
        name: "profile-view",
        params: { profileId: profile.id },
      });
    },
    getStatusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "approved":
          return "green darken-1";
        case "rejected":
          return "red darken-2";
        default:
          return "amber darken-2";
      }
    },

    formatStatus(status) {
      if (!status) return "Pending";
      const formatted = status.charAt(0).toUpperCase() + status.slice(1);
      return formatted;
    },
  },
};
</script>

<style scoped>
.option-card {
  border-radius: 16px;
  background-color: #fff;
  width: calc(100% - 2rem);
  margin: 0 auto 1.5rem auto;
}

.text--primary {
  color: #2a6465 !important;
}

.v-list-item {
  transition: background 0.2s ease;
}
.v-list-item:hover {
  background-color: #f2f9f3 !important;
}

.v-subheader {
  font-size: 16px;
  letter-spacing: 0.3px;
}

.v-btn {
  text-transform: none;
}

.v-container {
  padding-bottom: 100px !important;
}
.profile-list-item {
  transition: background 0.2s ease;
}
.profile-list-item:hover {
  background-color: #f9fdfb !important;
  cursor: pointer;
}

.status-badge {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 120px;
}

.rounded-circle {
  border-radius: 50%;
  object-fit: cover;
}
.v-progress-linear {
  transition: width 0.4s ease;
  opacity: 0.9;
}
</style>